// Relatórios › Leads de anúncio. Lista paginada + cards (summary) + export CSV
// sobre os MESMOS filtros — ver AdLeadsReport.vue.
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import AdReferralLeadsAPI, {
  backendErrorMessage,
} from 'dashboard/api/adReferralLeads';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';

export const types = {
  SET_RECORDS: 'SET_AD_LEADS_REPORT_RECORDS',
  SET_META: 'SET_AD_LEADS_REPORT_META',
  SET_SUMMARY: 'SET_AD_LEADS_REPORT_SUMMARY',
  SET_FILTERS: 'SET_AD_LEADS_REPORT_FILTERS',
  SET_UI_FLAG: 'SET_AD_LEADS_REPORT_UI_FLAG',
  SET_ERRORS: 'SET_AD_LEADS_REPORT_ERRORS',
  RESET: 'RESET_AD_LEADS_REPORT',
};

export const initialFilters = () => ({
  // epoch em SEGUNDOS, como o CallRecordDateFilter emite e o backend lê.
  from: null,
  to: null,
  channels: [],
  sourceIds: [],
  q: '',
  page: 1,
  perPage: 25,
  sort: 'created_at',
  order: 'desc',
});

// Quem muda a POPULAÇÃO — quais leads contam nos cards. Paginar e ordenar só
// recortam/reordenam a mesma população: refazer o summary nesses casos seria
// carga pura e faria os cards piscarem a cada troca de página.
export const POPULATION_KEYS = ['from', 'to', 'channels', 'sourceIds', 'q'];

const initialState = () => ({
  records: [],
  meta: { page: 1, perPage: 25, totalCount: 0, totalPages: 0 },
  summary: null,
  filters: initialFilters(),
  uiFlags: { fetchingList: false, fetchingSummary: false, exporting: false },
  // Mensagem do último fetch que falhou (null = ok). A página mostra na
  // tabela/cards em vez de deixar o dado do filtro anterior fingindo ser deste.
  errors: { list: null, summary: null },
});

// Filtros comuns aos três endpoints.
export const buildScopeParams = filters => {
  const params = { since: filters.from, until: filters.to };
  if (filters.channels.length) params.channels = [...filters.channels];
  if (filters.sourceIds.length) params.source_ids = [...filters.sourceIds];
  const q = filters.q.trim();
  if (q) params.q = q;
  return params;
};

export const buildListParams = filters => ({
  ...buildScopeParams(filters),
  page: filters.page,
  per_page: filters.perPage,
  sort: filters.sort,
  order: filters.order,
});

export const exportFileName = ({ from, to }) => {
  const day = epoch => format(fromUnixTime(epoch), 'yyyy-MM-dd');
  return `leads-anuncio-${day(from)}-a-${day(to)}.csv`;
};

const hasPeriod = filters => Boolean(filters.from && filters.to);

const errorMessage = error => backendErrorMessage(error) || error.message;

const sameValue = (a, b) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, i) => item === b[i]);
  }
  return a === b;
};

// Contador por endpoint: troca rápida de filtro dispara requests que podem
// voltar fora de ordem. Só a resposta do request MAIS NOVO entra no state —
// a antiga mostraria leads de um filtro que já não está na tela.
const requestSeq = { list: 0, summary: 0 };

export const getters = {
  getRecords: $state => $state.records,
  getMeta: $state => $state.meta,
  getSummary: $state => $state.summary,
  getFilters: $state => $state.filters,
  getUIFlags: $state => $state.uiFlags,
  getErrors: $state => $state.errors,
};

export const actions = {
  // Página montou: nada do relatório anterior (filtro, lista, cards) sobrevive.
  // O filtro de data não é controlado e volta pro padrão no mount — manter o
  // resto do filtro antigo deixaria a tela com dois períodos de verdade.
  reset({ commit }) {
    // Request em voo da visita anterior não pode pousar no state limpo.
    requestSeq.list += 1;
    requestSeq.summary += 1;
    commit(types.RESET);
  },

  async fetchList({ commit, state: $state }) {
    if (!hasPeriod($state.filters)) return;
    requestSeq.list += 1;
    const requestId = requestSeq.list;
    commit(types.SET_UI_FLAG, { fetchingList: true });
    try {
      const { data } = await AdReferralLeadsAPI.get(
        buildListParams($state.filters)
      );
      if (requestId !== requestSeq.list) return;
      commit(types.SET_RECORDS, data.payload);
      commit(types.SET_META, data.meta);
      commit(types.SET_ERRORS, { list: null });
    } catch (error) {
      if (requestId !== requestSeq.list) return;
      commit(types.SET_RECORDS, []);
      commit(types.SET_META, {
        page: 1,
        per_page: $state.filters.perPage,
        total_count: 0,
        total_pages: 0,
      });
      commit(types.SET_ERRORS, { list: errorMessage(error) });
    } finally {
      if (requestId === requestSeq.list) {
        commit(types.SET_UI_FLAG, { fetchingList: false });
      }
    }
  },

  async fetchSummary({ commit, state: $state }) {
    if (!hasPeriod($state.filters)) return;
    requestSeq.summary += 1;
    const requestId = requestSeq.summary;
    commit(types.SET_UI_FLAG, { fetchingSummary: true });
    try {
      const { data } = await AdReferralLeadsAPI.summary(
        buildScopeParams($state.filters)
      );
      if (requestId !== requestSeq.summary) return;
      commit(types.SET_SUMMARY, data);
      commit(types.SET_ERRORS, { summary: null });
    } catch (error) {
      if (requestId !== requestSeq.summary) return;
      commit(types.SET_SUMMARY, null);
      commit(types.SET_ERRORS, { summary: errorMessage(error) });
    } finally {
      if (requestId === requestSeq.summary) {
        commit(types.SET_UI_FLAG, { fetchingSummary: false });
      }
    }
  },

  fetchAll({ dispatch }) {
    return Promise.all([dispatch('fetchList'), dispatch('fetchSummary')]);
  },

  // Porta única de mudança de filtro. Regras:
  //  - mudou a população (período, canal, anúncio, busca) → lista + cards;
  //  - mudou só página/tamanho/ordenação → só a lista;
  //  - não mudou nada (reescolher o mesmo período) → recarrega os dois:
  //    é o gesto de "atualizar", e recarregar só a lista deixaria os cards
  //    contando uma população diferente da tabela.
  // Qualquer mudança que não seja de página volta pra página 1 — senão a
  // página 5 de uma população menor abre vazia.
  updateFilters({ commit, dispatch, state: $state }, partial) {
    const changed = Object.keys(partial).filter(
      key => !sameValue(partial[key], $state.filters[key])
    );
    const next = { ...partial };
    if (changed.some(key => key !== 'page') && !('page' in partial)) {
      next.page = 1;
    }
    commit(types.SET_FILTERS, next);

    if (!changed.length) return dispatch('fetchAll');
    if (changed.some(key => POPULATION_KEYS.includes(key))) {
      return dispatch('fetchAll');
    }
    return dispatch('fetchList');
  },

  // Rejeita com o erro original — quem chama (toast.promise) decide o texto
  // via backendErrorMessage. O botão fica desabilitado enquanto exporta; a
  // guarda aqui cobre o duplo clique que chega antes do re-render.
  async exportCsv({ commit, state: $state }, { timezone }) {
    if ($state.uiFlags.exporting || !hasPeriod($state.filters)) return false;
    commit(types.SET_UI_FLAG, { exporting: true });
    try {
      const response = await AdReferralLeadsAPI.export({
        ...buildScopeParams($state.filters),
        timezone,
      });
      downloadCsvFile(exportFileName($state.filters), response.data);
      return true;
    } finally {
      commit(types.SET_UI_FLAG, { exporting: false });
    }
  },
};

export const mutations = {
  [types.SET_RECORDS]($state, records) {
    $state.records = [...records];
  },
  [types.SET_META]($state, meta) {
    $state.meta = {
      page: Number(meta.page),
      perPage: Number(meta.per_page),
      totalCount: Number(meta.total_count),
      totalPages: Number(meta.total_pages),
    };
  },
  [types.SET_SUMMARY]($state, summary) {
    $state.summary = summary ? { ...summary } : null;
  },
  [types.SET_FILTERS]($state, partial) {
    $state.filters = { ...$state.filters, ...partial };
  },
  [types.SET_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.SET_ERRORS]($state, errors) {
    $state.errors = { ...$state.errors, ...errors };
  },
  [types.RESET]($state) {
    Object.assign($state, initialState());
  },
};

export default {
  namespaced: true,
  // Função (não objeto): cada store criado ganha o próprio state.
  state: initialState,
  getters,
  actions,
  mutations,
};
